import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFactory } from "@/core/hooks/useFactory";
import { useAuth } from "@/core/AuthContext";
import { useAppScope } from "@/core/AppScopeContext";
import { fetchChecklistsByFactoryId } from "@/modules/checklist/api";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Badge } from "@/shared/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Progress } from "@/shared/ui/progress";
import { toast } from "sonner";
import {
  Award,
  Users,
  FileCheck,
  AlertTriangle,
  Search,
  Plus,
  Grid3X3,
  List,
  ChevronRight,
  Calendar,
  Clock,
  User,
  Building2,
} from "lucide-react";
import { FactorySelector } from "@/core/components/FactorySelector";
import { CompetencyMatrix } from "@/core/components/competency/CompetencyMatrix";
import { UserCompetencyView } from "@/core/components/competency/UserCompetencyView";
import { CompetencyLibrary } from "@/core/components/competency/CompetencyLibrary";
import { AssignCompetencyDialog } from "@/core/components/competency/AssignCompetencyDialog";
import { OperatorCompetencyView } from "@/core/components/competency/OperatorCompetencyView";

interface Competency {
  id: string;
  name: string;
  description: string | null;
  color: string;
  is_global: boolean;
  factory_id: string | null;
}

interface UserCompetency {
  id: string;
  user_id: string;
  competency_id: string;
  factory_id: string;
  certified_at: string;
  expires_at: string | null;
  certified_by: string | null;
  training_date: string | null;
  training_hours: number | null;
  certificate_number: string | null;
  notes: string | null;
  status: string;
  competency: Competency;
  user?: { full_name: string | null; email: string };
  certifier?: { full_name: string | null; email: string } | null;
}

interface FactoryUser {
  id: string;
  full_name: string | null;
  email: string;
  job_title: string | null;
  department: string | null;
}

interface WorkInstruction {
  id: string;
  name: string;
  checklist_kind: "startup" | "shift_change";
  instruction_competencies: {
    competency: { id: string; name: string; color: string | null } | null;
  }[];
}

export default function CompetencyManagement() {
  const { selectedFactory, selectedFactoryId } = useFactory();
  const { user } = useAuth();
  const { effectiveMode, selectedTenantId } = useAppScope();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("matrix");
  const [searchQuery, setSearchQuery] = useState("");
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Fetch all competencies (global + factory-specific)
  const { data: competencies = [], isLoading: competenciesLoading } = useQuery({
    queryKey: ["competencies", selectedFactoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("competencies")
        .select("*")
        .or(`is_global.eq.true,factory_id.eq.${selectedFactoryId}`)
        .order("name");
      if (error) throw error;
      return data as Competency[];
    },
    enabled: !!selectedFactoryId,
  });

  // Fetch users in this factory
  const { data: factoryUsers = [], isLoading: usersLoading } = useQuery({
    queryKey: ["factory-users", selectedFactoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_factory_roles")
        .select(`
          user_id,
          profile:profiles!user_factory_roles_user_id_fkey(id, full_name, email, job_title, department)
        `)
        .eq("factory_id", selectedFactoryId)
        .eq("status", "approved");
      if (error) throw error;
      return (data || [])
        .map((d: any) => d.profile)
        .filter(Boolean) as FactoryUser[];
    },
    enabled: !!selectedFactoryId,
  });

  // Fetch user competencies for this factory
  const { data: userCompetencies = [], isLoading: userCompetenciesLoading } = useQuery({
    queryKey: ["user-competencies", selectedFactoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_competencies")
        .select(`
          *,
          competency:competencies(*)
        `)
        .eq("factory_id", selectedFactoryId);
      if (error) throw error;
      return (data || []).map((d: any) => ({
        ...d,
        user: undefined,
        certifier: undefined,
      })) as UserCompetency[];
    },
    enabled: !!selectedFactoryId,
  });

  // Fetch work instructions with required competencies
  const { data: workInstructions = [] } = useQuery({
    queryKey: [
      "mod_checklist",
      "work-instructions-competencies",
      selectedFactory?.tenant_id ?? null,
      selectedFactoryId,
    ],
    queryFn: async () => {
      if (!selectedFactoryId || !selectedFactory?.tenant_id) return [];

      const templates = await fetchChecklistsByFactoryId(
        selectedFactory.tenant_id,
        selectedFactoryId,
      );

      return templates
        .filter((template) => template.is_active)
        .map(
          (template): WorkInstruction => ({
            id: template.id,
            name: template.name,
            checklist_kind: template.checklist_kind,
            instruction_competencies: template.instruction_competencies ?? [],
          }),
        );
    },
    enabled: !!selectedFactoryId && !!selectedFactory?.tenant_id,
  });

  // Calculate statistics
  const stats = {
    totalUsers: factoryUsers.length,
    totalCompetencies: competencies.length,
    certifications: userCompetencies.filter((uc) => uc.status === "active").length,
    expiringSoon: userCompetencies.filter((uc) => {
      if (!uc.expires_at) return false;
      const daysUntilExpiry = Math.ceil(
        (new Date(uc.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      );
      return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
    }).length,
  };

  // Calculate competency coverage
  const competencyCoverage = competencies.map((comp) => {
    const certified = userCompetencies.filter(
      (uc) => uc.competency_id === comp.id && uc.status === "active"
    ).length;
    return {
      ...comp,
      certifiedCount: certified,
      coverage: factoryUsers.length > 0 ? (certified / factoryUsers.length) * 100 : 0,
    };
  });

  // Filter users by search
  const filteredUsers = factoryUsers.filter(
    (u) =>
      u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!selectedFactory) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Competency Management</h1>
          <p className="text-muted-foreground mt-1">
            Skills matrix, certifications, and training records
          </p>
        </div>
        <div className="flex items-center gap-4 p-6 bg-card rounded-xl border border-border">
          <Building2 className="w-8 h-8 text-muted-foreground" />
          <div>
            <p className="font-medium">Select a factory to view competencies</p>
            <p className="text-sm text-muted-foreground">
              Choose a factory from the header selector
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isLoading = competenciesLoading || usersLoading || userCompetenciesLoading;

  // Operator mode shows personal competency view
  if (effectiveMode === "operator") {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-foreground">My Certifications</h1>
          <p className="text-muted-foreground mt-1">
            Your skills, certifications, and training progress
          </p>
        </div>
        
        <OperatorCompetencyView
          currentUserId={user?.id || ""}
          userCompetencies={userCompetencies}
          competencies={competencies}
          workInstructions={workInstructions}
          isLoading={isLoading}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Competency Management</h1>
          <p className="text-muted-foreground mt-1">
            Skills matrix, certifications, and training records
          </p>
        </div>
        <Button
          className="gap-2"
          variant="industrial"
          onClick={() => setAssignDialogOpen(true)}
        >
          <Plus className="w-4 h-4" />
          Assign Competency
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalUsers}</p>
                <p className="text-xs text-muted-foreground">Team Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Award className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalCompetencies}</p>
                <p className="text-xs text-muted-foreground">Competencies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.certifications}</p>
                <p className="text-xs text-muted-foreground">Active Certs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.expiringSoon}</p>
                <p className="text-xs text-muted-foreground">Expiring Soon</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <TabsList>
            <TabsTrigger value="matrix" className="gap-2">
              <Grid3X3 className="w-4 h-4" />
              <span className="hidden sm:inline">Skills Matrix</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">By User</span>
            </TabsTrigger>
            <TabsTrigger value="library" className="gap-2">
              <Award className="w-4 h-4" />
              <span className="hidden sm:inline">Library</span>
            </TabsTrigger>
          </TabsList>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full sm:w-64"
            />
          </div>
        </div>

        <TabsContent value="matrix" className="space-y-4">
          <CompetencyMatrix
            competencies={competencyCoverage}
            users={filteredUsers}
            userCompetencies={userCompetencies}
            isLoading={isLoading}
            onAssign={(userId) => {
              setSelectedUserId(userId);
              setAssignDialogOpen(true);
            }}
          />
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <UserCompetencyView
            users={filteredUsers}
            userCompetencies={userCompetencies}
            competencies={competencies}
            workInstructions={workInstructions}
            isLoading={isLoading}
            onAssign={(userId) => {
              setSelectedUserId(userId);
              setAssignDialogOpen(true);
            }}
          />
        </TabsContent>

        <TabsContent value="library" className="space-y-4">
          <CompetencyLibrary
            competencies={competencies}
            competencyCoverage={competencyCoverage}
            workInstructions={workInstructions}
            searchQuery={searchQuery}
            tenantId={selectedTenantId}
            factoryId={selectedFactoryId!}
          />
        </TabsContent>
      </Tabs>

      {/* Assign Dialog */}
      <AssignCompetencyDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        users={factoryUsers}
        competencies={competencies}
        tenantId={selectedTenantId}
        factoryId={selectedFactoryId!}
        preselectedUserId={selectedUserId}
        currentUserId={user?.id}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["user-competencies"] });
          setSelectedUserId(null);
        }}
      />
    </div>
  );
}
